import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { components } from '@/api/generated/schema';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { formatDate, formatTime } from '@/lib/format';
import {
  addDays,
  parseIsoDate,
  parseWallClock,
  toIsoDate,
  todayInZone,
  zonedCivilDate,
  zonedTimeToInstant,
  zonedWallClock,
} from '@/lib/timezone';
import {
  RATE_TIERS,
  TimeEntryRefusedError,
  useDeleteTimeEntry,
  useJobTimeEntries,
  useLaborItems,
  useRunningTimer,
  useSaveTimeEntry,
  useStartTimer,
  useStopTimer,
  type LaborItem,
  type RateTier,
  type TimeEntry,
} from '@/routes/jobs/useJobTime';

type Job = components['schemas']['JobResponse'];
type Visit = components['schemas']['JobVisitResponse'];
type Technician = { id: string; name: string };

/** Work in progress takes a timer; finished work takes time by hand (the API's rule). */
const FINISHED = new Set(['Complete', 'Invoiced', 'Closed', 'Canceled']);

/** The API's cap on typed time: one entry covers at most a day (`LaborRates.MaxEnteredMinutes`). */
const MAX_TYPED_MINUTES = 24 * 60;

/**
 * A job's billable time (M2-33; Kyle, 2026-10-03): the technician's timer on
 * their visit, and the job's time list — added, corrected and removed by hand.
 *
 * <b>The timer is mobile-first</b>: one full-width, tall Start or Stop button,
 * the elapsed time large above it. It shows for someone booked on one of the
 * job's visits while the job is open. Elapsed time runs from the SERVER's
 * clock (`asOf`, the moment the answer was fetched), never the phone's — a
 * phone ten minutes fast still shows the time the API will bill.
 *
 * <b>Which labor item a timer starts with</b> (Kyle, Q2 = A): the person's
 * last labor item on this job, else the account's first active one; with none
 * at all the timer says to add one to the price book first.
 *
 * <b>Who changes what</b> is the API's answer (`canChange`, `canStop`, and the
 * list's `closedReason`), never re-derived here.
 */
export function JobTime({
  jobId,
  job,
  timeZone,
  canManage,
  technicians,
  onAnnounce,
}: {
  jobId: string;
  job: Job;
  timeZone: string;
  /** `customers.manage`: the office adds time for anyone. */
  canManage: boolean;
  /** Who the office may add time for (needs `users.read`; empty otherwise). */
  technicians: Technician[];
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const me = useCurrentUser().data?.userId;
  const time = useJobTimeEntries(jobId);
  const laborItems = useLaborItems();
  const [dialog, setDialog] = useState<TimeEntry | 'new' | null>(null);
  const [deleting, setDeleting] = useState<TimeEntry | null>(null);

  if (time.isError) {
    return null;
  }

  const entries = time.data?.entries ?? [];
  const running = entries.filter((entry) => entry.endedAt === null);
  const myVisits = job.visits.filter((visit) => me !== undefined && visit.assignedUserId === me);
  const totalHours = entries.reduce((sum, entry) => sum + Number(entry.hours ?? 0), 0);

  return (
    <div className="space-y-4 rounded-lg border p-4" data-testid="job-time">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t('timeEntries.heading')}</h2>
        {time.data && time.data.closedReason === null ? (
          <Button variant="outline" size="sm" onClick={() => setDialog('new')}>
            {t('timeEntries.add')}
          </Button>
        ) : null}
      </div>

      {myVisits.length > 0 && !FINISHED.has(job.status) ? (
        <Timer
          jobId={jobId}
          visits={myVisits}
          entries={entries}
          me={me!}
          laborItems={laborItems.data}
          timeZone={timeZone}
          onAnnounce={onAnnounce}
        />
      ) : null}

      {/* The API refuses to invoice a job while a timer runs on it; say so
          before anyone presses Create invoice. */}
      {running.length > 0 && job.status === 'Complete' ? (
        <p
          className="rounded-md border border-[var(--color-destructive)] p-2 text-sm"
          data-testid="running-timer-warning"
        >
          {t('timeEntries.runningWarning', {
            names: running.map((entry) => entry.userName).join(', '),
            count: running.length,
          })}
        </p>
      ) : null}

      {time.data?.closedReason ? (
        <p
          className="text-sm text-[var(--color-muted-foreground)]"
          data-testid="time-closed-reason"
        >
          {time.data.closedReason}
        </p>
      ) : null}

      {time.isPending ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('timeEntries.loading')}</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('timeEntries.empty')}</p>
      ) : (
        <>
          <ul className="divide-y" aria-label={t('timeEntries.listLabel')}>
            {entries.map((entry) => (
              <EntryRow
                key={entry.id}
                jobId={jobId}
                entry={entry}
                timeZone={timeZone}
                onEdit={() => setDialog(entry)}
                onDelete={() => setDeleting(entry)}
                onAnnounce={onAnnounce}
              />
            ))}
          </ul>
          <p className="text-sm font-medium" data-testid="time-total">
            {t('timeEntries.total', {
              hours: t('lines.hours', { count: totalHours, hours: round2(totalHours) }),
            })}
          </p>
        </>
      )}

      {dialog ? (
        <TimeEntryDialog
          jobId={jobId}
          entry={dialog === 'new' ? null : dialog}
          visits={job.visits}
          laborItems={laborItems.data ?? []}
          timeZone={timeZone}
          people={canManage && dialog === 'new' ? technicians.filter((p) => p.id !== me) : []}
          onClose={() => setDialog(null)}
          onSaved={(message) => {
            setDialog(null);
            onAnnounce(message);
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteEntry
          jobId={jobId}
          entry={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            onAnnounce(t('timeEntries.deleted'));
          }}
        />
      ) : null}
    </div>
  );
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * When an entry ended, on the account's clock: the time alone on the day it
 * started, and the date too when it ran past midnight — a timer has no cap,
 * and "8:00 AM–9:00 AM" under a 25-hour figure would read as an hour.
 */
function endText(startedAt: string, endedAt: string, timeZone: string): string {
  const sameDay =
    toIsoDate(zonedCivilDate(new Date(startedAt), timeZone)) ===
    toIsoDate(zonedCivilDate(new Date(endedAt), timeZone));
  return sameDay
    ? formatTime(endedAt, timeZone)
    : formatDate(endedAt, { dateStyle: 'medium', timeStyle: 'short', timeZone });
}

/** The account's first active labor item: its standard one if marked, else the first by name. */
function firstLaborItem(items: LaborItem[]): string {
  return (items.find((item) => item.isStandardLabor) ?? items[0])?.id ?? '';
}

/** "1:05:09" — hours, minutes, seconds of a running timer. */
function elapsedText(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Milliseconds since `startedAt` on the SERVER's clock: the server's `asOf`
 * when the answer arrived, plus how long the page has been open since. A
 * device clock that is wrong moves both ends together and cancels out.
 */
function useServerElapsed(
  startedAt: string | undefined,
  asOf: string | undefined,
  fetchedAt: number,
) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [startedAt]);
  if (!startedAt || !asOf) return 0;
  // ⚠️ `now` is the last tick, which can be from BEFORE this answer arrived (the
  // page was open before the timer started, or a refetch just landed). A
  // negative "time since the answer" would subtract from the server's figure
  // until the next tick (found in review), so it counts as none.
  return Date.parse(asOf) - Date.parse(startedAt) + Math.max(0, now - fetchedAt);
}

function Timer({
  jobId,
  visits,
  entries,
  me,
  laborItems,
  timeZone,
  onAnnounce,
}: {
  jobId: string;
  visits: Visit[];
  entries: TimeEntry[];
  me: string;
  laborItems: LaborItem[] | undefined;
  timeZone: string;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const running = useRunningTimer();
  const start = useStartTimer(jobId);
  const stop = useStopTimer(jobId);
  const [problem, setProblem] = useState<string | null>(null);

  const mine = running.data?.entry ?? null;
  const elapsed = useServerElapsed(mine?.startedAt, running.data?.asOf, running.dataUpdatedAt);

  // Q2 = A: the person's last labor item on this job, if still offered; else the first active one.
  const defaultItem = useMemo(() => {
    const offered = laborItems ?? [];
    const last = [...entries]
      .filter((entry) => entry.userId === me)
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
      .find((entry) => offered.some((item) => item.id === entry.catalogItemId));
    // "First" is the standard labor item when the book marks one (Q8), else the first by name.
    return last?.catalogItemId ?? firstLaborItem(offered);
  }, [entries, laborItems, me]);

  const [itemId, setItemId] = useState('');
  const [tier, setTier] = useState<RateTier>('Standard');
  const [visitId, setVisitId] = useState(visits[0]?.id ?? '');
  const chosenItem = itemId || defaultItem;

  if (running.isPending || laborItems === undefined) {
    return null;
  }

  if (mine && mine.jobId !== jobId) {
    return (
      <div className="rounded-md bg-[var(--color-muted)] p-3 text-sm" data-testid="timer-elsewhere">
        <p>{t('timeEntries.timer.elsewhere')}</p>
        <Link to={`/jobs/${mine.jobId}`} className="underline underline-offset-2">
          {t('timeEntries.timer.openThatJob')}
        </Link>
      </div>
    );
  }

  if (!mine && laborItems.length === 0) {
    return (
      <p className="rounded-md bg-[var(--color-muted)] p-3 text-sm" data-testid="timer-no-labor">
        {t('timeEntries.timer.noLaborItems')}
      </p>
    );
  }

  const busy = start.isPending || stop.isPending;

  function startTimer() {
    setProblem(null);
    start.mutate(
      { visitId, catalogItemId: chosenItem, rateTier: tier, notes: null },
      {
        onSuccess: () => onAnnounce(t('timeEntries.timer.started')),
        onError: (err) =>
          setProblem(
            err instanceof TimeEntryRefusedError
              ? t('timeEntries.timer.startRefused', { reason: err.message })
              : t('timeEntries.timer.startFailed'),
          ),
      },
    );
  }

  function stopTimer(entryId: string) {
    setProblem(null);
    stop.mutate(entryId, {
      onSuccess: (stopped) =>
        onAnnounce(
          t('timeEntries.timer.stopped', {
            hours: t('lines.hours', { count: Number(stopped.hours), hours: Number(stopped.hours) }),
          }),
        ),
      onError: () => setProblem(t('timeEntries.timer.stopFailed')),
    });
  }

  // ⚠️ ONE button, the same element across Start → Stop → Start (found in
  // review): two buttons in two branches meant the one pressed was unmounted
  // and focus fell to the page, so a keyboard or screen-reader user was thrown
  // out of the timer by the press. `aria-disabled` rather than `disabled` while
  // the request is out, for the same reason — a disabled button loses focus.
  return (
    <div className="space-y-3 rounded-md bg-[var(--color-muted)] p-3" data-testid="timer">
      {mine ? (
        <div className="space-y-2">
          <p className="text-sm">
            {t('timeEntries.timer.runningSince', {
              time: formatTime(mine.startedAt, timeZone),
              item: mine.laborItemName,
            })}
            {mine.rateTier !== 'Standard' ? ` · ${t(`timeEntries.tier.${mine.rateTier}`)}` : ''}
          </p>
          {/* A label a screen reader reads, in words (aria-label is not
              allowed on a paragraph); the figure itself is not a live region,
              which would announce every second. */}
          <p className="text-center text-4xl font-semibold tabular-nums">
            <span className="sr-only">{t('timeEntries.timer.elapsedLabel')} </span>
            <span data-testid="timer-elapsed">{elapsedText(elapsed)}</span>
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor={`${id}-item`} className="text-sm font-medium">
              {t('timeEntries.fields.laborItem')}
            </label>
            <select
              id={`${id}-item`}
              className={FIELD_CLASS}
              value={chosenItem}
              onChange={(event) => setItemId(event.target.value)}
            >
              {laborItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${id}-tier`} className="text-sm font-medium">
              {t('timeEntries.fields.tier')}
            </label>
            <select
              id={`${id}-tier`}
              className={FIELD_CLASS}
              value={tier}
              onChange={(event) => setTier(event.target.value as RateTier)}
            >
              {RATE_TIERS.map((value) => (
                <option key={value} value={value}>
                  {t(`timeEntries.tier.${value}`)}
                </option>
              ))}
            </select>
          </div>
          {visits.length > 1 ? (
            <div>
              <label htmlFor={`${id}-visit`} className="text-sm font-medium">
                {t('timeEntries.fields.visit')}
              </label>
              <select
                id={`${id}-visit`}
                className={FIELD_CLASS}
                value={visitId}
                onChange={(event) => setVisitId(event.target.value)}
              >
                {visits.map((visit) => (
                  <option key={visit.id} value={visit.id}>
                    {formatDate(visit.scheduledStart, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                      timeZone,
                    })}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      )}
      {problem ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}
      <Button
        className="h-14 w-full text-lg aria-disabled:opacity-50"
        variant={mine ? 'destructive' : 'default'}
        aria-disabled={busy || undefined}
        onClick={() => {
          if (busy) return;
          if (mine) stopTimer(mine.id);
          else startTimer();
        }}
      >
        {mine
          ? stop.isPending
            ? t('timeEntries.timer.stopping')
            : t('timeEntries.timer.stop')
          : start.isPending
            ? t('timeEntries.timer.starting')
            : t('timeEntries.timer.start')}
      </Button>
    </div>
  );
}

function EntryRow({
  jobId,
  entry,
  timeZone,
  onEdit,
  onDelete,
  onAnnounce,
}: {
  jobId: string;
  entry: TimeEntry;
  timeZone: string;
  onEdit: () => void;
  onDelete: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const stop = useStopTimer(jobId);
  const [problem, setProblem] = useState<string | null>(null);
  const running = entry.endedAt === null;

  return (
    <li className="space-y-1 py-3" data-testid="time-entry" data-entry-id={entry.id}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{entry.userName}</span>
        <span className="tabular-nums" data-testid="time-entry-hours">
          {running
            ? t('timeEntries.running')
            : t('lines.hours', { count: Number(entry.hours), hours: Number(entry.hours) })}
        </span>
      </div>
      {/* One clock: the date and both times on the account's, never the device's. */}
      <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="time-entry-when">
        {formatDate(entry.startedAt, { dateStyle: 'medium', timeZone })} ·{' '}
        {formatTime(entry.startedAt, timeZone)}
        {entry.endedAt ? `–${endText(entry.startedAt, entry.endedAt, timeZone)}` : ''}
      </p>
      <p className="text-sm" data-testid="time-entry-item">
        {entry.laborItemName}
        {entry.rateTier !== 'Standard' ? ` · ${t(`timeEntries.tier.${entry.rateTier}`)}` : ''}
      </p>
      {entry.notes ? <p className="text-sm">{entry.notes}</p> : null}
      {entry.billedInvoiceNumber !== null ? (
        <p className="text-xs text-[var(--color-muted-foreground)]" data-testid="time-entry-billed">
          {t('timeEntries.billedOn', { number: entry.billedInvoiceNumber })}
        </p>
      ) : null}
      {problem ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}
      {entry.canStop || entry.canChange ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {entry.canStop ? (
            <Button
              size="sm"
              variant="destructive"
              disabled={stop.isPending}
              onClick={() => {
                setProblem(null);
                stop.mutate(entry.id, {
                  onSuccess: () =>
                    onAnnounce(t('timeEntries.stoppedFor', { name: entry.userName })),
                  onError: () => setProblem(t('timeEntries.timer.stopFailed')),
                });
              }}
            >
              {t('timeEntries.stopFor', { name: entry.userName })}
            </Button>
          ) : null}
          {entry.canChange ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onEdit}
                aria-label={t('timeEntries.editLabel', {
                  name: entry.userName,
                  when: formatDate(entry.startedAt, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                    timeZone,
                  }),
                })}
              >
                {t('timeEntries.edit')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onDelete}
                aria-label={t('timeEntries.deleteLabel', {
                  name: entry.userName,
                  when: formatDate(entry.startedAt, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                    timeZone,
                  }),
                })}
              >
                {t('timeEntries.delete')}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

type EndMode = 'end' | 'duration';

/** Add time by hand, or correct an entry — the API's total statement, on the account's clock. */
function TimeEntryDialog({
  jobId,
  entry,
  visits,
  laborItems,
  timeZone,
  people,
  onClose,
  onSaved,
}: {
  jobId: string;
  entry: TimeEntry | null;
  visits: Visit[];
  laborItems: LaborItem[];
  timeZone: string;
  /** Others the office may add time for; empty means "the caller". */
  people: Technician[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const titleId = useId();
  const save = useSaveTimeEntry(jobId, entry?.id);
  const running = entry !== null && entry.endedAt === null;

  const [userId, setUserId] = useState('');
  const [date, setDate] = useState(
    toIsoDate(entry ? zonedCivilDate(new Date(entry.startedAt), timeZone) : todayInZone(timeZone)),
  );
  const [startTime, setStartTime] = useState(
    entry ? zonedWallClock(new Date(entry.startedAt), timeZone) : '',
  );
  const [mode, setMode] = useState<EndMode>('end');
  const [endTime, setEndTime] = useState(
    entry?.endedAt ? zonedWallClock(new Date(entry.endedAt), timeZone) : '',
  );
  const [minutes, setMinutes] = useState('');
  // An archived item stays on the entry that names it (the API keeps it as a residence).
  const offered =
    entry && !laborItems.some((item) => item.id === entry.catalogItemId)
      ? [...laborItems, { id: entry.catalogItemId, name: entry.laborItemName } as LaborItem]
      : laborItems;
  // ⚠️ Chosen at submit, not frozen at open: Add can be pressed before the labor
  // items have loaded, and an initial '' then refused the save with "Add a labor
  // item to the price book first." while the picker showed one (found in review).
  const [pickedItem, setItemId] = useState(entry?.catalogItemId ?? '');
  const itemId = pickedItem || firstLaborItem(laborItems);
  const [tier, setTier] = useState<RateTier>((entry?.rateTier as RateTier) ?? 'Standard');
  const [visitId, setVisitId] = useState(entry?.visitId ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [problem, setProblem] = useState<string | null>(null);

  // What the boxes were filled with, so a correction that leaves the times alone
  // sends them back exactly as they were (seconds and all).
  const [initial] = useState(() => ({ date, startTime, endTime }));

  function instant(day: string, clock: string, laterBy = 0): string | null {
    const civil = parseIsoDate(day);
    const minuteOfDay = parseWallClock(clock);
    if (civil === null || minuteOfDay === null) return null;
    return zonedTimeToInstant(
      addDays(civil, laterBy),
      Math.floor(minuteOfDay / 60),
      minuteOfDay % 60,
      timeZone,
    ).toISOString();
  }

  function submit() {
    setProblem(null);
    const sameDate = entry !== null && date === initial.date;
    const startedAt =
      sameDate && startTime === initial.startTime ? entry.startedAt : instant(date, startTime);
    if (startedAt === null) {
      setProblem(t('timeEntries.form.needStart'));
      return;
    }
    let endedAt: string | null = null;
    let duration: number | null = null;
    if (mode === 'end' && endTime !== '') {
      // ⚠️ An end at or before the start is the next morning (found in review):
      // a timer that ran 11 PM to 1:30 AM came back refused on a notes-only
      // edit, its end put on the day it started. Untouched, it is sent as it was.
      endedAt =
        sameDate && endTime === initial.endTime && entry.endedAt
          ? entry.endedAt
          : instant(date, endTime);
      if (endedAt !== null && Date.parse(endedAt) <= Date.parse(startedAt)) {
        endedAt = instant(date, endTime, 1);
      }
    } else if (mode === 'duration' && minutes.trim() !== '') {
      duration = Number(minutes);
      if (!Number.isInteger(duration) || duration < 1 || duration > MAX_TYPED_MINUTES) {
        setProblem(t('timeEntries.form.wholeMinutes'));
        return;
      }
    } else if (!running) {
      setProblem(t('timeEntries.form.needEnd'));
      return;
    }
    if (!itemId) {
      setProblem(t('timeEntries.timer.noLaborItems'));
      return;
    }

    save.mutate(
      {
        ...(entry === null ? { userId: userId || null } : {}),
        visitId: visitId || null,
        catalogItemId: itemId,
        rateTier: tier,
        startedAt,
        endedAt,
        minutes: duration,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => onSaved(entry ? t('timeEntries.saved') : t('timeEntries.added')),
        // The API's own sentence, verbatim: it knows the rule it applied.
        onError: (err) =>
          setProblem(
            err instanceof TimeEntryRefusedError ? err.message : t('timeEntries.form.saveFailed'),
          ),
      },
    );
  }

  return (
    <Dialog
      titleId={titleId}
      title={entry ? t('timeEntries.form.editTitle') : t('timeEntries.form.addTitle')}
      onClose={onClose}
      testId="time-entry-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? t('timeEntries.form.saving') : t('timeEntries.form.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {people.length > 0 ? (
          <div>
            <label htmlFor={`${id}-who`} className="text-sm font-medium">
              {t('timeEntries.fields.who')}
            </label>
            <select
              id={`${id}-who`}
              className={FIELD_CLASS}
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
            >
              <option value="">{t('timeEntries.fields.me')}</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={`${id}-date`} className="text-sm font-medium">
              {t('timeEntries.fields.date')}
            </label>
            <input
              id={`${id}-date`}
              type="date"
              className={FIELD_CLASS}
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor={`${id}-start`} className="text-sm font-medium">
              {t('timeEntries.fields.start')}
            </label>
            <input
              id={`${id}-start`}
              type="time"
              className={FIELD_CLASS}
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
          </div>
        </div>
        <fieldset>
          <legend className="text-sm font-medium">{t('timeEntries.fields.howLong')}</legend>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`${id}-mode`}
                checked={mode === 'end'}
                onChange={() => setMode('end')}
              />
              {t('timeEntries.fields.byEnd')}
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`${id}-mode`}
                checked={mode === 'duration'}
                onChange={() => setMode('duration')}
              />
              {t('timeEntries.fields.byDuration')}
            </label>
          </div>
        </fieldset>
        {mode === 'end' ? (
          <div>
            <label htmlFor={`${id}-end`} className="text-sm font-medium">
              {t('timeEntries.fields.end')}
            </label>
            <input
              id={`${id}-end`}
              type="time"
              className={FIELD_CLASS}
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
            />
            {running ? (
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {t('timeEntries.form.keepRunning')}
              </p>
            ) : null}
          </div>
        ) : (
          <div>
            <label htmlFor={`${id}-minutes`} className="text-sm font-medium">
              {t('timeEntries.fields.minutes')}
            </label>
            <input
              id={`${id}-minutes`}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_TYPED_MINUTES}
              step={1}
              className={FIELD_CLASS}
              value={minutes}
              onChange={(event) => setMinutes(event.target.value)}
            />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={`${id}-item`} className="text-sm font-medium">
              {t('timeEntries.fields.laborItem')}
            </label>
            <select
              id={`${id}-item`}
              className={FIELD_CLASS}
              value={itemId}
              onChange={(event) => setItemId(event.target.value)}
            >
              {offered.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${id}-tier`} className="text-sm font-medium">
              {t('timeEntries.fields.tier')}
            </label>
            <select
              id={`${id}-tier`}
              className={FIELD_CLASS}
              value={tier}
              onChange={(event) => setTier(event.target.value as RateTier)}
            >
              {RATE_TIERS.map((value) => (
                <option key={value} value={value}>
                  {t(`timeEntries.tier.${value}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {visits.length > 0 ? (
          <div>
            <label htmlFor={`${id}-visit`} className="text-sm font-medium">
              {t('timeEntries.fields.visit')}
            </label>
            <select
              id={`${id}-visit`}
              className={FIELD_CLASS}
              value={visitId}
              onChange={(event) => setVisitId(event.target.value)}
            >
              <option value="">{t('timeEntries.fields.noVisit')}</option>
              {visits.map((visit) => (
                <option key={visit.id} value={visit.id}>
                  {formatDate(visit.scheduledStart, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                    timeZone,
                  })}
                  {visit.assignedUserName ? ` · ${visit.assignedUserName}` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div>
          <label htmlFor={`${id}-notes`} className="text-sm font-medium">
            {t('timeEntries.fields.notes')}
          </label>
          <textarea
            id={`${id}-notes`}
            className={`${FIELD_CLASS} h-20`}
            maxLength={1000}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
        {problem ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {problem}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function DeleteEntry({
  jobId,
  entry,
  onClose,
  onDeleted,
}: {
  jobId: string;
  entry: TimeEntry;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const remove = useDeleteTimeEntry(jobId);
  return (
    <ConfirmDialog
      titleId={titleId}
      title={t('timeEntries.deleteTitle')}
      description={t('timeEntries.deleteDescription', { name: entry.userName })}
      confirmLabel={t('timeEntries.delete')}
      isPending={remove.isPending}
      errorMessage={
        remove.error
          ? remove.error instanceof TimeEntryRefusedError
            ? remove.error.message
            : t('timeEntries.deleteFailed')
          : undefined
      }
      onConfirm={() => remove.mutate(entry.id, { onSuccess: onDeleted })}
      onClose={() => {
        remove.reset();
        onClose();
      }}
      testId="delete-time-entry-dialog"
    />
  );
}
